import React, { useState, useRef, useCallback } from 'react';
import { AuthenticatedTemplate, useMsal } from '@azure/msal-react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Avatar,
  Button,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  Text,
  Link,
  OverlayDrawer,
} from '@fluentui/react-components';
import {
  SignOutRegular,
  DismissRegular,
  CopyRegular,
  CheckmarkRegular,
  DocumentBulletListRegular,
} from '@fluentui/react-icons';
import { useImpersonationNavigate } from '../../services/hooks/useImpersonationNavigate';
import { useHeaderStyles } from './Header.styles';
import { HeaderProps } from './Header.types';
import { clearAuthProvider } from '../../config';
import { appInsights } from '../../telemetry/appInsights';
import { usePrehireData } from '../../services/hooks/usePrehireData';
import { clearStoredImpersonationEmail } from '../../utils/impersonation';
import JobLogo from '../../assets/job.svg';
import MailSvg from '../../assets/mail.svg';
import ReplaySvg from '../../assets/replay.svg';
import AskHRSvg from '../../assets/askHR.svg';

export const Header: React.FC<HeaderProps> = ({ viewMode, onViewModeChange, onReplayFRE, showActionsInTestMode = false }) => {
  const { instance } = useMsal();
  const styles = useHeaderStyles();
  const navigate = useImpersonationNavigate();
  const { data: prehireData } = usePrehireData();
  const ActionsWrapper = showActionsInTestMode ? React.Fragment : AuthenticatedTemplate;

  const handleLogout = () => {
    // Clear the auth provider preference on logout
    clearAuthProvider();
    clearStoredImpersonationEmail();
    // Set flag to force showing sign-in selection after logout
    sessionStorage.setItem('forceSignInSelection', 'true');
    instance
      .logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      })
      .catch((error) => console.log(error));
  };

  const caseId = prehireData?.crmCaseId || '';
  const candidateName = prehireData?.candidate?.fullName || prehireData?.candidate?.firstName || '';
  const [isSupportPanelOpen, setIsSupportPanelOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [caseIdCopied, setCaseIdCopied] = useState(false);
  const supportButtonRef = useRef<HTMLButtonElement>(null);

  const closeSupportPanel = useCallback(() => {
    setIsSupportPanelOpen(false);
    // Use requestAnimationFrame to ensure focus is set after the drawer closes
    requestAnimationFrame(() => {
      supportButtonRef.current?.focus();
    });
  }, []);

  const mailtoHref = (() => {
    const subject = `Pre-Hire Activity Enquiry – Case Number ${caseId || '[Insert Case Number]'}`;
    const body = [
      'Hello,',
      '',
      `My name is ${candidateName || '[Candidate Name]'}, and I am reaching out regarding my pre-hire activities.`,
      '',
      `My reference case number is ${caseId || '[Insert Case Number]'}.`,
      '',
      'I would appreciate your assistance with [briefly describe the issue you are facing].',
      '',
      'Thank you,',
      candidateName || '[Candidate Name]',
      '[Phone Number – optional]',
    ].join('\n');
    return `mailto:askhr@microsoft.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  })();

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText('askhr@microsoft.com');
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy email:', err);
    }
  };

  const handleCopyCaseId = async () => {
    if (!caseId) return;
    try {
      await navigator.clipboard.writeText(caseId);
      setCaseIdCopied(true);
      setTimeout(() => setCaseIdCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy case ID:', err);
    }
  };

  return (
    <header role="banner" className={styles.header}>
      <div className={styles.brand}>
        <img src={JobLogo} alt="Microsoft" width="32" height="32" />
        <span className={styles.title}>New Hire Hub</span>
      </div>
      <ActionsWrapper>
        <div className={styles.actions}>
          {/* <button
            className={styles.supportButton}
            aria-label="Browse policies"
            title="Browse policies"
            onClick={() => {
              appInsights.trackEvent(
                { name: 'PolicyBrowseClicked' },
                { component: 'Header', action: 'click' },
              );
              navigate('/policies');
            }}
          >
            <DocumentBulletListRegular fontSize={20} />
          </button> */}
          <button
            className={styles.supportButton}
            aria-label="Replay walkthrough"
            title="Replay walkthrough"
            onClick={(event) => {
              appInsights.trackEvent(
                { name: 'ReplayWalkthroughClicked' },
                { component: 'Header', action: 'click' },
              );
              onReplayFRE?.(event.currentTarget);
            }}
          >
            <img className={styles.contrastThemeIcon} src={ReplaySvg} alt="" width="20" height="20" />
            <span className={styles.contrastThemeLabel} aria-hidden="true">Replay walkthrough</span>
          </button>
          <button
            ref={supportButtonRef}
            className={styles.askHRButton}
            aria-label="Ask HR"
            title="Ask HR"
            onClick={() => {
              appInsights.trackEvent(
                { name: 'SupportPanelOpened' },
                { component: 'Header', action: 'click' },
              );
              setIsSupportPanelOpen(true);
            }}
          >
            <img className={styles.contrastThemeIcon} src={AskHRSvg} alt="" height="16" />
            <span className={styles.contrastThemeLabel} aria-hidden="true">Ask HR</span>
          </button>
          <Menu>
            <MenuTrigger>
              <Avatar
                className={styles.avatar}
                color="colorful"
                name={prehireData?.candidate?.firstName || 'User'}
                aria-label={`${prehireData?.candidate?.firstName || 'User'}, open menu`}
                size={28}
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem icon={<SignOutRegular />} onClick={handleLogout}>
                  Sign Out
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
        </div>
      </ActionsWrapper>

      <OverlayDrawer
        open={isSupportPanelOpen}
        onOpenChange={(_, { open }) => !open && closeSupportPanel()}
        position="end"
        size="medium"
        style={{ top: '48px', height: 'calc(100vh - 48px)', width: '460px' }}
        modalType="modal"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close help panel"
                icon={<DismissRegular />}
                onClick={closeSupportPanel}
              />
            }
          >
            Need help?
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody className={styles.supportPanelBody}>
              <div className={styles.emailRow}>
                <Text as="p" size={400} className={styles.supportInstruction}>
                  Email AskHR at{' '}
                  <Link
                    href={mailtoHref}
                    className={styles.inlineLink}
                    aria-label="Email AskHR at askhr@microsoft.com"
                    onClick={() => {
                      appInsights.trackEvent(
                        { name: 'AskHRMailLinkClicked' },
                        { component: 'Header', action: 'click' },
                      );
                    }}
                  >
                    <strong>askhr@microsoft.com</strong>
                  </Link>
                </Text>
                <Button
                  appearance="subtle"
                  size="small"
                  icon={emailCopied ? <CheckmarkRegular /> : <CopyRegular />}
                  onClick={handleCopyEmail}
                  aria-label={emailCopied ? 'AskHR email address copied' : 'Copy AskHR email address'}
                />
                {emailCopied && (
                  <Text aria-hidden="true" className={styles.copyStatus}>
                    Copied
                  </Text>
                )}
              </div>
              <div className={styles.supportInstructionRow}>
                <Text as="p" className={styles.visuallyHidden}>
                  For faster response include your reference number in the subject line,{' '}
                  {caseId ? <strong>{caseId}</strong> : <strong>your reference number</strong>}
                  , or simply reply to any AskHR email you've already received.
                </Text>
                <div className={styles.supportPanelText}>
                  <span aria-hidden="true">
                    For faster response include your reference number in the subject line,{' '}
                  </span>
                  {caseId ? (
                    <span className={styles.caseIdAction}>
                      <strong aria-hidden="true">{caseId}</strong>
                      <Button
                        appearance="subtle"
                        size="small"
                        icon={caseIdCopied ? <CheckmarkRegular /> : <CopyRegular />}
                        onClick={handleCopyCaseId}
                        aria-label={caseIdCopied ? 'Reference number copied' : `Copy reference number ${caseId}`}
                      />
                      {caseIdCopied && (
                        <Text aria-hidden="true" className={styles.copyStatus}>
                          Copied
                        </Text>
                      )}
                    </span>
                  ) : (
                    <strong aria-hidden="true">your reference number</strong>
                  )}
                  <span aria-hidden="true">
                    , or simply reply to any AskHR email you've already received.
                  </span>
                </div>
              </div>
              <img src={MailSvg} alt="" className={styles.supportPanelImage} />
        </DrawerBody>
        <div className={styles.supportPanelFooter}>
          <Button appearance="secondary" onClick={closeSupportPanel}>
            Close
          </Button>
        </div>
      </OverlayDrawer>
    </header>
  );
};
